import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiBase, authHeaders, resolveOrgId } from '../../utils/authHeaders';
import { useMobileAuth } from '../MobileAuthProvider';
import { timeAgo } from '../../notifications/format';

const MobileNotifications = () => {
  const { token } = useMobileAuth();
  const [scope, setScope] = useState(() => ({ orgId: resolveOrgId(),
    siteId: localStorage.getItem('current_location_id') || '' }));
  useEffect(() => {
    const updateScope = () => setScope({ orgId: resolveOrgId(),
      siteId: localStorage.getItem('current_location_id') || '' });
    for (const event of ['locationChanged', 'organizationChanged', 'storage'])
      window.addEventListener(event, updateScope);
    return () => {
      for (const event of ['locationChanged', 'organizationChanged', 'storage'])
        window.removeEventListener(event, updateScope);
    };
  }, []);
  return <ScopedNotifications key={JSON.stringify([token, scope.orgId, scope.siteId])}
    token={token} {...scope} />;
};

const ScopedNotifications = ({ token, orgId, siteId }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const active = useRef(false);
  const loadRevision = useRef(0);
  const isCurrent = useCallback(() => active.current && resolveOrgId() === orgId &&
    (localStorage.getItem('current_location_id') || '') === siteId, [orgId, siteId]);

  const load = useCallback(async () => {
    const revision = ++loadRevision.current;
    if (!token || !orgId || !siteId) {
      setItems([]);
      setError(siteId ? '' : 'Select a site to view notifications.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${apiBase()}/api/mobile/notifications`, {
        headers: authHeaders({ token, orgId, locationId: siteId }),
      });
      if (!response.ok) throw new Error(`Could not load notifications (${response.status})`);
      const data = await response.json();
      if (!isCurrent() || revision !== loadRevision.current) return;
      setItems(Array.isArray(data.notifications) ? data.notifications : []);
    } catch (err) {
      if (isCurrent() && revision === loadRevision.current) setError(err.message || 'Could not load notifications');
    } finally {
      if (isCurrent() && revision === loadRevision.current) setLoading(false);
    }
  }, [siteId, orgId, token, isCurrent]);

  useEffect(() => {
    active.current = true;
    load();
    return () => {
      active.current = false;
      loadRevision.current += 1;
    };
  }, [load]);

  const markRead = async (item) => {
    if (!isCurrent()) return;
    setBusyId(item.id);
    setError('');
    try {
      const response = await fetch(`${apiBase()}/api/mobile/notifications/${encodeURIComponent(item.id)}/read`, {
        method: 'POST', headers: authHeaders({ token, orgId, locationId: siteId }),
      });
      if (!response.ok) throw new Error(`Could not update notification (${response.status})`);
      if (!isCurrent()) return;
      setItems((current) => current.map((row) => row.id === item.id ? { ...row, read: true } : row));
      window.dispatchEvent(new Event('notificationsChanged'));
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Could not update notification');
    } finally {
      if (isCurrent()) setBusyId('');
    }
  };

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-5" aria-labelledby="mobile-notifications-title">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h1 id="mobile-notifications-title" className="flex items-center gap-2 text-xl font-semibold text-ink"><Bell aria-hidden="true" className="h-5 w-5" />Notifications</h1>
        <button type="button" onClick={load} disabled={loading} aria-label="Refresh notifications" className="inline-flex h-10 w-10 items-center justify-center border border-surface-border bg-surface text-ink disabled:opacity-50">
          <RefreshCw aria-hidden="true" className="h-4 w-4" />
        </button>
      </header>
      {error && <p role="alert" className="mb-3 text-sm text-danger">{error}</p>}
      {loading ? <p role="status" className="text-sm text-ink-muted">Loading notifications…</p> : null}
      {!loading && !error && items.length === 0 ? <p className="border-y border-surface-border py-5 text-sm text-ink-muted">No notifications for this site.</p> : null}
      <ul className="divide-y divide-surface-border">
        {items.map((item) => (
          <li key={item.id} className={`py-4 ${item.read ? 'text-ink-muted' : 'text-ink'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{item.title}</p>
                <p className="mt-1 break-words text-sm">{item.message}</p>
                <time className="mt-2 block text-xs text-ink-muted" dateTime={item.created_at}>{timeAgo(item.created_at)}</time>
                {item.kind === 'inspection_corrective_action_assigned' && <Link className="mt-2 inline-block text-sm font-medium text-primary-700 underline" to="/mobile/safety/inspections">Open inspection tasks</Link>}
                {item.kind === 'vision_concern_assigned' && <Link className="mt-2 inline-block text-sm font-medium text-primary-700 underline" to={item.concern_id ? `/mobile/visual-safety?concern=${encodeURIComponent(item.concern_id)}` : '/mobile/visual-safety'}>Open safety check</Link>}
                {item.kind === 'operandi_human_context_question' && <Link className="mt-2 inline-block text-sm font-medium text-primary-700 underline" to={item.run_id ? `/process-discovery?runId=${encodeURIComponent(item.run_id)}` : '/process-discovery'}>Open process discovery</Link>}
              </div>
              {!item.read && <button type="button" onClick={() => markRead(item)} disabled={busyId === item.id} aria-label={`Mark ${item.title} as read`} className="inline-flex h-10 w-10 shrink-0 items-center justify-center border border-surface-border bg-surface text-ink disabled:opacity-50"><Check aria-hidden="true" className="h-4 w-4" /></button>}
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
};

export default MobileNotifications;
